import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleQuestion,
  faBuilding,
  faFileLines,
  faBook,
  faArrowUp,
  faArrowDown,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import { AdminService } from "../../../services/admin.service";
import "./dashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

/* ----------------------------- helpers ------------------------------- */
// API subject names come in lowercase / loosely formatted (e.g. "english grammer ").
const titleCase = (value: string = "") =>
  value.trim().replace(/\b\w/g, (c) => c.toUpperCase());

// Format a "vs last month" change with an explicit + / - sign.
const formatDelta = (value: number = 0) => `${value >= 0 ? "+" : ""}${value}`;

// Slice colours for the subject distribution doughnut.
const DOUGHNUT_COLORS = [
  "#2563eb",
  "#60a5fa",
  "#93c5fd",
  "#a78bfa",
  "#cbd5e1",
  "#34d399",
  "#f59e0b",
];

/* ------------------------- static chart options ---------------------- */
const lineOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      display: true,
      position: "bottom",
      labels: { usePointStyle: true, boxWidth: 8, padding: 18, color: "#6b7280", font: { size: 12 } },
    },
    tooltip: {
      backgroundColor: "#111827",
      padding: 12,
      cornerRadius: 8,
      titleColor: "#fff",
      bodyColor: "#e5e7eb",
      usePointStyle: true,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { display: false },
      ticks: { color: "#9ca3af", font: { size: 11 } },
    },
    y: {
      grid: { color: "#f1f5f9" },
      border: { display: false },
      ticks: { color: "#9ca3af", font: { size: 11 }, maxTicksLimit: 5 },
    },
  },
};

const doughnutOptions: any = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "68%",
  plugins: {
    legend: {
      position: "bottom",
      labels: { usePointStyle: true, boxWidth: 8, padding: 14, color: "#6b7280", font: { size: 12 } },
    },
    tooltip: {
      backgroundColor: "#111827",
      padding: 12,
      cornerRadius: 8,
      callbacks: {
        // Show the raw question count plus its share of the total.
        label: (c: any) => {
          const value = Number(c.parsed) || 0;
          const total = (c.dataset?.data || []).reduce(
            (sum: number, v: any) => sum + (Number(v) || 0),
            0
          );
          const pct = total ? ((value / total) * 100).toFixed(1) : "0.0";
          return ` ${c.label}: ${value.toLocaleString()} (${pct}%)`;
        },
      },
    },
  },
};

// Build the small trend sparkline shown on each KPI card.
function sparkConfig(values: number[], up: boolean) {
  const color = up ? "#16a34a" : "#dc2626";
  return {
    data: {
      labels: values.map((_, i) => i),
      datasets: [
        {
          data: values,
          borderColor: color,
          backgroundColor: up ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)",
          fill: true,
          tension: 0.45,
          borderWidth: 2,
          pointRadius: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: false } },
      scales: { x: { display: false }, y: { display: false } },
      elements: { line: { borderJoinStyle: "round" } },
    } as any,
  };
}

/* ------------------------------ component ---------------------------- */
export default function DashboardOverview() {
  const [dashboard, setDashboard] = useState<any>();

  const getDashboardStats = async () => {
    await AdminService.getDashboardStats().then((res: any) => {
      setDashboard(res.data);
    });
  };

  useEffect(() => {
    getDashboardStats();
  }, []);

  const stats = dashboard?.stats;
  const vsLastMonth = stats?.vsLastMonth;
  const questionsGrowth: any[] = dashboard?.questionsGrowth ?? [];
  const growthPercentage: number = dashboard?.growthPercentage ?? 0;
  const subjectDistribution: any[] = dashboard?.subjectDistribution ?? [];
  const topSubjects: any[] = dashboard?.topSubjects ?? [];
  const recentCompanies: any[] = dashboard?.recentCompanies ?? [];

  // KPI cards. Sparklines are only shown where we have a monthly series.
  const kpis = [
    {
      label: "Total Questions",
      value: stats?.totalQuestions,
      delta: vsLastMonth?.questions,
      icon: faCircleQuestion,
      spark: questionsGrowth.map((g) => g.questions),
    },
    {
      label: "Total Companies",
      value: stats?.totalCompanies,
      delta: vsLastMonth?.companies,
      icon: faBuilding,
      spark: null,
    },
    {
      label: "Question Papers",
      value: stats?.totalQuestionPapers,
      delta: vsLastMonth?.papers,
      icon: faFileLines,
      spark: questionsGrowth.map((g) => g.papers),
    },
    {
      label: "Total Subjects",
      value: stats?.totalSubjects,
      delta: vsLastMonth?.subjects,
      icon: faBook,
      spark: null,
    },
  ];

  // Questions / papers added over the last 12 months.
  const lineData = {
    labels: questionsGrowth.map((g) => g.month),
    datasets: [
      {
        label: "Questions",
        data: questionsGrowth.map((g) => g.questions),
        borderColor: "#2563eb",
        backgroundColor: (ctx: any) => {
          const { ctx: c, chartArea } = ctx.chart;
          if (!chartArea) return "rgba(37,99,235,0.12)";
          const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          g.addColorStop(0, "rgba(37,99,235,0.25)");
          g.addColorStop(1, "rgba(37,99,235,0.00)");
          return g;
        },
        fill: true,
        tension: 0.4,
        borderWidth: 2.5,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: "#2563eb",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
      },
      {
        label: "Papers",
        data: questionsGrowth.map((g) => g.papers),
        borderColor: "#94a3b8",
        backgroundColor: "transparent",
        fill: false,
        tension: 0.4,
        borderWidth: 2,
        borderDash: [5, 4],
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  // Subject distribution doughnut: show the top slices, fold the rest into "Others".
  const topDistribution = subjectDistribution.slice(0, 6);
  const othersCount = subjectDistribution
    .slice(6)
    .reduce((sum, d) => sum + (Number(d.count) || 0), 0);
  const doughnutLabels = topDistribution.map((d) => titleCase(d.name));
  const doughnutValues = topDistribution.map((d) => d.count);
  if (othersCount > 0) {
    doughnutLabels.push("Others");
    doughnutValues.push(othersCount);
  }
  const doughnutData = {
    labels: doughnutLabels,
    datasets: [
      {
        data: doughnutValues,
        backgroundColor: DOUGHNUT_COLORS,
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  // Top subjects bar meter is relative to the highest count.
  const maxSubjectCount = Number(topSubjects[0]?.count) || 1;

  return (
    <>
      <div className="ds-page-head">
        <div>
          <h1 className="ds-page-title">Welcome back, Super Admin </h1>
          <p className="ds-page-sub">Here's what's happening across your platform today.</p>
        </div>
      </div>

      {/* KPI ROW */}
      <div className="ds-kpi-grid">
        {kpis.map((kpi, i) => {
          const up = (kpi.delta ?? 0) >= 0;
          const spark = kpi.spark ? sparkConfig(kpi.spark, up) : null;
          return (
            <div className="ds-kpi dash-kpi" key={i}>
              <div className="ds-kpi-top">
                <span className="ds-kpi-label">{kpi.label}</span>
                <span className="ds-kpi-icon">
                  <FontAwesomeIcon icon={kpi.icon} />
                </span>
              </div>
              <div className="dash-kpi-row">
                <div>
                  <div className="ds-kpi-value">
                    {Number(kpi.value ?? 0).toLocaleString()}
                  </div>
                  <span className={`dash-trend ${up ? "dash-trend--up" : "dash-trend--down"}`}>
                    <FontAwesomeIcon icon={up ? faArrowUp : faArrowDown} />
                    {formatDelta(kpi.delta)}
                    <span className="dash-trend-note">vs last month</span>
                  </span>
                </div>
                {spark && (
                  <div className="dash-kpi-spark">
                    <Line data={spark.data} options={spark.options} />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* CHART ROW */}
      <div className="ds-stats-grid mt-3">
        <div className="ds-card ds-card-pad">
          <div className="dash-card-head">
            <div>
              <div className="ds-cell-strong">Questions Growth</div>
              <div className="ds-cell-label">Questions & papers added over the last 12 months</div>
            </div>
            <span className={`ds-badge ${growthPercentage >= 0 ? "ds-badge--success" : "ds-badge--danger"}`}>
              <FontAwesomeIcon icon={growthPercentage >= 0 ? faArrowUp : faArrowDown} />{" "}
              {Math.abs(growthPercentage)}%
            </span>
          </div>
          <div className="dash-chart-lg">
            <Line data={lineData} options={lineOptions} />
          </div>
        </div>

        <div className="ds-card ds-card-pad">
          <div className="dash-card-head">
            <div>
              <div className="ds-cell-strong">Subject Distribution</div>
              <div className="ds-cell-label">Share of total questions</div>
            </div>
          </div>
          <div className="dash-chart-lg">
            <Doughnut data={doughnutData} options={doughnutOptions} />
          </div>
        </div>
      </div>

      {/* RECENT COMPANIES + TOP SUBJECTS ROW */}
      <div className="ds-stats-grid mt-3">
        <div className="ds-card">
          <div className="dash-card-head dash-card-head--pad">
            <div className="ds-cell-strong">Recent Companies</div>
          </div>
          <div className="ds-thead dash-thead">
            <div style={{ flex: "1 1 220px" }}>Company</div>
            <div style={{ width: "110px" }}>Users</div>
            <div style={{ width: "100px" }} className="text-end">Status</div>
          </div>
          {recentCompanies.map((c, i) => {
            const blocked = c.blocked;
            return (
              <div className="ds-trow" key={c._id ?? i}>
                <div style={{ flex: "1 1 220px", display: "flex", gap: "0.75rem", alignItems: "center", minWidth: 0 }}>
                  <span className="ds-avatar">{(c.companyName ?? "").slice(0, 2)}</span>
                  <div style={{ minWidth: 0 }}>
                    <div className="ds-cell-strong text-truncate">{c.companyName}</div>
                    <div className="ds-cell-label text-truncate">{c.email}</div>
                  </div>
                </div>
                <div style={{ width: "110px" }} className="ds-cell-value">
                  {c.users}/{c.maxUsers}
                </div>
                <div style={{ width: "100px" }} className="d-flex justify-content-end">
                  <span className={`ds-badge ${blocked ? "ds-badge--danger" : "ds-badge--success"}`}>
                    <span className="ds-dot" />
                    {blocked ? "Blocked" : "Active"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="ds-card ds-card-pad">
          <div className="dash-card-head">
            <div>
              <div className="ds-cell-strong">Top Subjects</div>
              <div className="ds-cell-label">Ranked by number of questions</div>
            </div>
          </div>
          <div className="dash-rank">
            {topSubjects.map((s, i) => (
              <div className="dash-rank-item" key={s._id ?? i}>
                <span className="dash-rank-no">{i + 1}</span>
                <div className="dash-rank-main">
                  <div className="dash-rank-top">
                    <span className="ds-cell-value">{titleCase(s.name)}</span>
                    <span className="ds-cell-muted">{Number(s.count ?? 0).toLocaleString()}</span>
                  </div>
                  <div className="ds-meter">
                    <span style={{ width: `${(Number(s.count) / maxSubjectCount) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
